export const calculateBaseScale = (rect, imageLayer) => {
    if (!imageLayer) return 1;
    const img = imageLayer.data;
    const scaleWidth = rect.width / img.width;
    const scaleHeight = rect.height / img.height;
    return Math.min(scaleWidth, scaleHeight);
};

export const isPointInShape = (x, y, shape, scale) => {
    switch (shape.type) {
        case 'marker': {
            // For marker, check if point is within circle
            const radius = shape.width / 2;
            const centerX = shape.startX + radius;
            const centerY = shape.startY + radius;
            const distance = Math.sqrt(
                Math.pow(x - centerX, 2) + 
                Math.pow(y - centerY, 2)
            );
            return distance <= radius;
        }
        
        case 'line': {
            // For lines, check if point is near the line segment
            const tolerance = Math.max(shape.lineWidth, 8);
            return isPointNearLine(
                x, y,
                shape.startX,
                shape.startY,
                shape.startX + shape.width,
                shape.startY + shape.height,
                tolerance
            );
        }
        
        case 'freedraw': {
            // For freedraw, check if point is near any line segment
            const tolerance = Math.max(shape.lineWidth, 8); // Use at least 8px tolerance for easier clicking
            
            // First check bounding box for performance
            const points = shape.points;
            if (!points || points.length === 0) return false;
            
            let minX = points[0].x;
            let maxX = points[0].x;
            let minY = points[0].y;
            let maxY = points[0].y;
            
            points.forEach(p => {
                minX = Math.min(minX, p.x);
                maxX = Math.max(maxX, p.x);
                minY = Math.min(minY, p.y);
                maxY = Math.max(maxY, p.y);
            });
            
            // Add tolerance to bounding box
            if (!(x >= minX - tolerance && 
                  x <= maxX + tolerance && 
                  y >= minY - tolerance && 
                  y <= maxY + tolerance)) {
                return false;
            }
            
            // If within bounding box, check each line segment
            for (let i = 1; i < points.length; i++) {
                const p1 = points[i - 1];
                const p2 = points[i];
                if (isPointNearLine(x, y, p1.x, p1.y, p2.x, p2.y, tolerance)) {
                    return true;
                }
            }
            return false;
        }
        
        case 'textbox': {
            // For textbox, check if point is within box bounds
            return (
                x >= shape.startX &&
                x <= shape.startX + shape.width &&
                y >= shape.startY &&
                y <= shape.startY + shape.height
            );
        }
        
        default: {
            // For other shapes (rectangle, oval), check bounding box
            return (
                x >= shape.startX &&
                x <= shape.startX + (shape.width || 0) &&
                y >= shape.startY &&
                y <= shape.startY + (shape.height || 0)
            );
        }
    }
};

export const getResizeHandle = (e, shape, finalScale, rect, centerX, centerY, position) => {
    const mouseX = ((e.clientX - rect.left) / finalScale) - centerX/finalScale - position.x/finalScale;
    const mouseY = ((e.clientY - rect.top) / finalScale) - centerY/finalScale - position.y/finalScale;
    
    const handleSize = 8 / finalScale;
    const boxX = shape.startX;
    const boxY = shape.startY;
    const boxWidth = shape.width || 0;
    const boxHeight = shape.height || 0;

    console.log('Mouse position:', mouseX, mouseY); // Debug log
    console.log('Center handle position:', 
        boxX + boxWidth/2, 
        boxY + boxHeight/2
    ); // Debug log

    const handles = [
        // Center handle first for priority
        { x: boxX + boxWidth/2, y: boxY + boxHeight/2, type: 'center' },
        // Corner handles
        { x: boxX, y: boxY, type: 'corner-tl' },
        { x: boxX + boxWidth, y: boxY, type: 'corner-tr' },
        { x: boxX, y: boxY + boxHeight, type: 'corner-bl' },
        { x: boxX + boxWidth, y: boxY + boxHeight, type: 'corner-br' },
        
        // Edge handles
        { x: boxX, y: boxY + boxHeight/2, type: 'edge-h' },
        { x: boxX + boxWidth, y: boxY + boxHeight/2, type: 'edge-h-right' },
        { x: boxX + boxWidth/2, y: boxY, type: 'edge-v' },
        { x: boxX + boxWidth/2, y: boxY + boxHeight, type: 'edge-v-bottom' },
    ];

    for (const handle of handles) {
        if (Math.abs(mouseX - handle.x) < handleSize && 
            Math.abs(mouseY - handle.y) < handleSize) {
            console.log('Handle detected:', handle.type); // Debug log
            return handle.type;
        }
    }
    return null;
};

// Helper function to check if a point is near a line segment
const isPointNearLine = (x, y, x1, y1, x2, y2, tolerance) => {
    const A = x - x1;
    const B = y - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) {
        param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
        xx = x1;
        yy = y1;
    } else if (param > 1) {
        xx = x2;
        yy = y2;
    } else {
        xx = x1 + param * C;
        yy = y1 + param * D;
    }

    const dx = x - xx;
    const dy = y - yy;

    return Math.sqrt(dx * dx + dy * dy) <= tolerance;
};

export const calculateShapeCoordinates = (e, rect, centerX, centerY, position, baseScale, scale) => {
    const finalScale = baseScale * scale;
    // Try reversing the order of operations
    return {
        x: ((e.clientX - rect.left) / finalScale) - centerX/finalScale - position.x/finalScale,
        y: ((e.clientY - rect.top) / finalScale) - centerY/finalScale - position.y/finalScale
    };
}; 